/**
 * InsightPro™ — Home page router
 *
 * The "/" route renders different landing pages based on the tenant's
 * solution:
 *   - MultiEntityFinance → Group Consolidated (exec command centre)
 *   - RetailPOS or default → retail Dashboard (pbiQuery-driven KPIs)
 *
 * The solution isn't stored on the userMemberships doc (see adminFunctions.js
 * line 509), so we fetch it from companies/{companyId} on mount when missing.
 * Cached in CompanyContext via setSelectedCompany so subsequent navigations
 * skip the round-trip.
 */
import React, { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useCompany } from '../context/CompanyContext';
import Dashboard from './Dashboard';
import GroupConsolidated from './GroupConsolidated';

const db = getFirestore();

export default function HomePage() {
  const { selectedCompany, setSelectedCompany } = useCompany();
  const companyId = selectedCompany?.id || selectedCompany?.companyId;
  const [solution, setSolution] = useState(selectedCompany?.solution || null);
  const [resolving, setResolving] = useState(!selectedCompany?.solution);

  useEffect(() => {
    if (selectedCompany?.solution) {
      setSolution(selectedCompany.solution);
      setResolving(false);
      return;
    }
    if (!companyId) { setResolving(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'companies', companyId));
        if (cancelled) return;
        const sol = (snap.exists() ? snap.data().solution : null) || 'RetailPOS';
        setSolution(sol);
        // Cache on the membership snapshot so we don't re-fetch on every nav
        setSelectedCompany({ ...selectedCompany, solution: sol });
      } catch {
        if (!cancelled) setSolution('RetailPOS');
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, selectedCompany, setSelectedCompany]);

  if (resolving) {
    return (
      <div style={{ padding: 40, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
        Loading…
      </div>
    );
  }

  if (solution === 'MultiEntityFinance') {
    return <GroupConsolidated />;
  }
  return <Dashboard />;
}
